import Router from 'next/router';
import { ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { filter, mergeMap, withLatestFrom } from 'rxjs/operators';
import { EnquiriesActionTypes } from '@bridebook/web/lib/enquiries/action-types';
import { SearchActionTypes } from '@bridebook/web/lib/search/action-types';
import { ShortlistActionTypes } from '@bridebook/web/lib/shortlist/action-types';
import { SupplierActions } from '@bridebook/web/lib/supplier/actions-types';
import { WeddingActionTypes } from '@bridebook/web/lib/weddings/action-types';
import { markTodayTaskAsCompleted, weddingTasksLoaded } from '@bridebook/web/lib/task/actions';
import { isTodayTaskCompleted } from '@bridebook/web/lib/task/selectors';
import { TaskActionTypes } from '@bridebook/web/lib/task/action-types';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { selectCurrentUser } from '@bridebook/web/lib/users/selectors';
import { Action, IApplicationState, IEpic } from '@bridebook/web/lib/types';
import { isSupplierVenue } from '@bridebook/web/lib/supplier/utils';
import { TTodayTaskStorageKey } from '@bridebook/web/lib/task/types';
import {
  forceAbTestVariant,
  setAbTestsLoaded,
  setAbTestVariant,
} from '@bridebook/toolbox/src/ab-testing';
import {
  ARRANGE_VIEWING_TASK_DEFINITION,
  ASSIGNMENT_VENUE_NOT_SELECTED_TASK_DEFINITION,
  ASSIGNMENT_VENUE_SELECTED_TASK_DEFINITION,
  EXPLORE_SUPPLIERS_TASK_DEFINITION,
  REACH_OUT_SUPPLIERS_TASK_DEFINITION,
  REACH_OUT_VENUES_TASK_DEFINITION,
  START_EXPLORING_VENUES_TASK_DEFINITION,
} from '@bridebook/web/components/task/today-task/mobile/task-definitions';
import {
  getMobileAssignmentFromTasks,
  resolveMobileAssignment,
} from '@bridebook/web/components/task/today-task/mobile/assignment';
import {
  incrementSearchCount,
  markFiltersApplied,
  incrementProfileViews,
  markFavourited,
  isEngagementComplete,
} from '@bridebook/web/components/task/today-task/mobile/engagement-tracker';

// Mobile TT flow is the only mobile experience (post LIVE-25845 rollout).
const isMobileTTActive = (state: IApplicationState) => getScreenType(state).isMobile;

const shouldMarkTask = (
  state: IApplicationState,
  taskId: string,
  storage: TTodayTaskStorageKey,
) => {
  if (storage === 'none') return false;
  const { status } = state.task.todayTasks;
  if (status !== 'loaded' && status !== 'partiallyLoaded') return false;
  return !isTodayTaskCompleted(state, { id: taskId, storage });
};

type EngagementTarget = {
  taskDef: typeof START_EXPLORING_VENUES_TASK_DEFINITION;
  userId: string;
};

const getEngagementTarget = (
  state: IApplicationState,
  isVenueAction: boolean,
): EngagementTarget | null => {
  const assignment = resolveMobileAssignment(state);
  const userId = selectCurrentUser(state)?.id;
  if (!userId) return null;

  if (assignment === 'venueNotSelected' && isVenueAction) {
    const taskDef = START_EXPLORING_VENUES_TASK_DEFINITION;
    if (!shouldMarkTask(state, taskDef.id, taskDef.storage)) return null;
    return { taskDef, userId };
  }

  if (assignment === 'venueSelected' && !isVenueAction) {
    const taskDef = EXPLORE_SUPPLIERS_TASK_DEFINITION;
    if (!shouldMarkTask(state, taskDef.id, taskDef.storage)) return null;
    return { taskDef, userId };
  }

  return null;
};

export const mobileSearchInteractionEpic: IEpic<Action, any> = (
  action$: Observable<Action>,
  { state$ },
) =>
  action$.pipe(
    ofType<Action>(
      SearchActionTypes.FETCH_SEARCH_SUCCESS,
      SearchActionTypes.UPDATE_SEARCH_FILTERS,
      SupplierActions.VIEWED_SUPPLIER_PROFILE_ANALYTICS,
      ShortlistActionTypes.SAVE_TO_SHORTLIST,
    ),
    withLatestFrom(state$),
    filter(([, state]) => isMobileTTActive(state)),
    mergeMap(([action, state]) => {
      const type = action.type;

      if (
        type === SearchActionTypes.FETCH_SEARCH_SUCCESS ||
        type === SearchActionTypes.UPDATE_SEARCH_FILTERS
      ) {
        const slug = state.search.request.slug;
        const isVenue = slug === 'venue';
        const target = getEngagementTarget(state, isVenue);
        if (!target) return EMPTY;

        if (type === SearchActionTypes.FETCH_SEARCH_SUCCESS) {
          incrementSearchCount(target.userId, target.taskDef.id);
        } else {
          // Skip page-init filter syncs (URL → Redux), only count user-applied filters
          const filterPayload = (
            action as { payload?: { skipAnalytics?: boolean } | Array<{ skipAnalytics?: boolean }> }
          ).payload;
          const entries = Array.isArray(filterPayload)
            ? filterPayload
            : filterPayload
              ? [filterPayload]
              : [];
          const hasUserAppliedFilter = entries.some((e) => !e.skipAnalytics);
          if (!hasUserAppliedFilter) return EMPTY;

          markFiltersApplied(target.userId, target.taskDef.id);
        }

        return isEngagementComplete(target.userId, target.taskDef.id)
          ? of(markTodayTaskAsCompleted(target.taskDef))
          : EMPTY;
      }

      if (type === SupplierActions.VIEWED_SUPPLIER_PROFILE_ANALYTICS) {
        const supplier = (action as { payload?: { supplier?: any } }).payload?.supplier;
        if (!supplier) return EMPTY;
        const isVenue = isSupplierVenue(supplier);
        const target = getEngagementTarget(state, isVenue);
        if (!target) return EMPTY;

        incrementProfileViews(target.userId, target.taskDef.id);
        return isEngagementComplete(target.userId, target.taskDef.id)
          ? of(markTodayTaskAsCompleted(target.taskDef))
          : EMPTY;
      }

      if (type === ShortlistActionTypes.SAVE_TO_SHORTLIST) {
        const payload = (action as { payload?: any }).payload;
        if (!payload) return EMPTY;
        const isVenue = isSupplierVenue(payload);
        const target = getEngagementTarget(state, isVenue);
        if (!target) return EMPTY;

        markFavourited(target.userId, target.taskDef.id);
        return isEngagementComplete(target.userId, target.taskDef.id)
          ? of(markTodayTaskAsCompleted(target.taskDef))
          : EMPTY;
      }

      return EMPTY;
    }),
  );

export const mobileEnquirySuccessEpic: IEpic<Action, any> = (
  action$: Observable<Action>,
  { state$ },
) =>
  action$.pipe(
    ofType(EnquiriesActionTypes.SEND_ENQUIRY_SUCCESS),
    withLatestFrom(state$),
    filter(([, state]) => isMobileTTActive(state)),
    mergeMap(([action, state]) => {
      const payload = (
        action as {
          payload?: {
            type?: string | string[];
            intent?: { showround?: boolean; moreAvailability?: boolean };
          };
        }
      ).payload;
      if (!payload) return EMPTY;
      const isVenue = isSupplierVenue(payload as Parameters<typeof isSupplierVenue>[0]);
      const assignment = resolveMobileAssignment(state);

      if (assignment === 'venueNotSelected' && isVenue) {
        const actions = [];

        if (
          shouldMarkTask(
            state,
            REACH_OUT_VENUES_TASK_DEFINITION.id,
            REACH_OUT_VENUES_TASK_DEFINITION.storage,
          )
        ) {
          actions.push(markTodayTaskAsCompleted(REACH_OUT_VENUES_TASK_DEFINITION));
        }

        if (
          (payload.intent?.showround || payload.intent?.moreAvailability) &&
          shouldMarkTask(
            state,
            ARRANGE_VIEWING_TASK_DEFINITION.id,
            ARRANGE_VIEWING_TASK_DEFINITION.storage,
          )
        ) {
          actions.push(markTodayTaskAsCompleted(ARRANGE_VIEWING_TASK_DEFINITION));
        }

        return actions.length > 0 ? from(actions) : EMPTY;
      }

      if (assignment === 'venueSelected' && !isVenue) {
        return shouldMarkTask(
          state,
          REACH_OUT_SUPPLIERS_TASK_DEFINITION.id,
          REACH_OUT_SUPPLIERS_TASK_DEFINITION.storage,
        )
          ? of(markTodayTaskAsCompleted(REACH_OUT_SUPPLIERS_TASK_DEFINITION))
          : EMPTY;
      }

      return EMPTY;
    }),
  );

export const mobileAssignmentEpic: IEpic<Action, any> = (action$: Observable<Action>, { state$ }) =>
  action$.pipe(
    ofType(
      TaskActionTypes.INIT_TODAY_TASK_LISTENER,
      setAbTestVariant.type,
      forceAbTestVariant.type,
      setAbTestsLoaded.type,
      weddingTasksLoaded.type,
      ShortlistActionTypes.FETCH_USER_SHORTLIST_SUCCESS,
      // Re-evaluate after onboarding sets bookings.venue
      WeddingActionTypes.UPDATE_WEDDING,
      // Leaving onboarding is the moment the path guard below starts passing: without this
      // trigger the first assignment waits for whatever action happens to come next.
      'ROUTE_CHANGE_COMPLETE',
    ),
    withLatestFrom(state$),
    filter(([, state]) => state.weddings.loaded && Boolean(state.weddings.profile.id)),
    filter(([, state]) => state.abTesting.abTestsLoaded),
    filter(([, state]) => state.shortlist.loaded),
    filter(([, state]) => isMobileTTActive(state)),
    filter(() => !Router?.pathname?.includes('/onboarding')),
    filter(([, state]) => {
      const todayTasks = state.task.todayTasks;
      return (
        (todayTasks.status === 'loaded' || todayTasks.status === 'partiallyLoaded') &&
        Boolean(todayTasks.wedding)
      );
    }),
    filter(([, state]) => !getMobileAssignmentFromTasks(state)),
    mergeMap(([, state]) => {
      const assignment = resolveMobileAssignment(state);
      return of(
        markTodayTaskAsCompleted(
          assignment === 'venueSelected'
            ? ASSIGNMENT_VENUE_SELECTED_TASK_DEFINITION
            : ASSIGNMENT_VENUE_NOT_SELECTED_TASK_DEFINITION,
        ),
      );
    }),
  );
